import { graphql } from 'gatsby';
import { applyPaddingCSS } from '@/utils/generatePadding';
import slugify from '@/utils/slugify';
import Link from '@/components/Link/Link';
import {
  StyledCtaContainer,
  StyledDescription,
  StyledEyebrow,
  StyledHeadline,
  StyledContainer,
  StyledSection,
  StyledTextContainer,
  StyledAnnouncementButton,
  StyledInfoTag,
} from './SectionIntroduction.styles';
import isRichTextValid from './../RichTextBlock/utils/isRichTextValid';
import { type BlockHTMLId } from '@/utils/renderBlock';
import { lazy, Suspense } from 'react';
import NavigationDropdown from '../NavigationDropdown/NavigationDropdown';
import ImageContainer from './components/ImageContainer/ImageContainer';
import InfoTag from '../InfoTag/InfoTag';
const RichTextBlock = lazy(() => import('@/components/RichTextBlock/RichTextBlock'));

const SectionIntroduction = ({
  sectionIntroductionElements: elements,
  system,
  id,
}: Queries.SECTION_INTRODUCTIONFragment & BlockHTMLId) => {
  const {
    eyebrow,
    headline,
    description,
    cta,
    image,
    text_alignment,
    images_options,
    text_size,
    visibility,
    background_color,
    announcement_button,
    tag,
  } = elements;
  const eyebrowValue = eyebrow?.value;
  const headlineValue = headline.value;
  const ctaValue = cta?.value;
  const imageOptions = images_options?.value ?? [];
  const isImageAlignLeft = imageOptions.includes('left_aligned');
  const hasShadowcastBackground = imageOptions.includes('shadowcast_background');
  const hasImage = !!image?.value;
  const tagValue = tag?.value;
  const textSize = text_size.value;
  const hasBackgroundColor = !!background_color?.value;
  const hasDescriptionValue = isRichTextValid(description?.value);
  const hasAnnouncementButton = !!announcement_button?.value;
  const hasEyebrow = typeof eyebrowValue === 'string' && eyebrowValue.trim().length > 0;
  const hasInfoTag = !!tag?.value;
  const shouldDisplayEyebrow = hasEyebrow && !hasInfoTag;
  let imageLayout: 'withImageShadowcast' | 'withImage' | 'textOnly' = 'textOnly';
  if (hasImage && hasShadowcastBackground) {
    imageLayout = 'withImageShadowcast';
  } else if (hasImage) {
    imageLayout = 'withImage';
  }

  return (
    <StyledSection
      css={applyPaddingCSS(elements)}
      hideOnMobile={visibility?.value === 'mobile_hidden'}
      data-codename={system.codename}
      hasBackgroundColor={hasBackgroundColor}
      id={id}
    >
      <StyledContainer
        align={text_alignment.value}
        isReversed={isImageAlignLeft}
        hasShadowcastBackground={hasShadowcastBackground}
        size={textSize}
        hasDescription={hasDescriptionValue}
      >
        <StyledTextContainer
          align={text_alignment.value}
          imageLayout={imageLayout}
          isReversed={isImageAlignLeft}
        >
          {hasAnnouncementButton && (
            <StyledAnnouncementButton
              hasBackgroundColor={hasBackgroundColor}
              align={text_alignment.value}
              {...announcement_button.value}
            />
          )}
          {hasInfoTag ? (
            <StyledInfoTag>
              <InfoTag {...tagValue!} />
            </StyledInfoTag>
          ) : null}
          {shouldDisplayEyebrow && <StyledEyebrow>{eyebrowValue}</StyledEyebrow>}
          <StyledHeadline id={slugify(headlineValue)}>{headlineValue}</StyledHeadline>
          {hasDescriptionValue && (
            <StyledDescription>
              <Suspense>
                <RichTextBlock
                  elements={{
                    rich_text_content: description,
                  }}
                />
              </Suspense>
            </StyledDescription>
          )}
          {ctaValue && (
            <StyledCtaContainer
              align={text_alignment.value}
              hasShadowcastBackground={hasShadowcastBackground}
            >
              {ctaValue.__typename === 'kontent_item_link' ? (
                <Link {...ctaValue} variant="primary" />
              ) : (
                <NavigationDropdown key={ctaValue.system.id} {...ctaValue} />
              )}
            </StyledCtaContainer>
          )}
        </StyledTextContainer>
        {hasImage && (
          <ImageContainer
            image={image}
            hasShadowcastBackground={hasShadowcastBackground}
            isReversed={isImageAlignLeft}
          />
        )}
      </StyledContainer>
    </StyledSection>
  );
};
export default SectionIntroduction;

export const fragment = graphql`
  fragment SECTION_INTRODUCTION on kontent_item_section_introduction {
    system {
      codename
    }
    sectionIntroductionElements: elements {
      ...COMPONENT_A_B_TEST
      ...COMPONENT_PERSONALIZATION
      ...PADDING
      visibility {
        value
      }
      image {
        value {
          width
          url
          type
          height
          name
          description
        }
      }
      announcement_button {
        value {
          ...ANNOUNCEMENT_BUTTON
        }
      }
      eyebrow {
        value
      }
      tag {
        value {
          ...INFO_TAG
        }
      }
      text_alignment {
        value
      }
      images_options {
        value
      }
      headline {
        value
      }
      text_size {
        value
      }
      description {
        value
        images {
          description
          height
          url
          image_id
          width
        }
        links {
          link_id
          codename
        }
        modular_content {
          ...CODE_SNIPPET
          ...TABLE
          ...VIDEO
          ...QUOTE
        }
      }
      cta {
        value {
          __typename
          ... on kontent_item {
            system {
              id
            }
          }
          ...LINK
          ...NAVIGATION_DROPDOWN
        }
      }
      background_color {
        value
      }
    }
  }
`;
